import {
  AppBskyRichtextFacet,
  type RichText,
  UnicodeString,
} from '@atproto/api'

import {type VerifiedUser} from '#/lib/api/verified'
import {toShortUrl} from './url-helpers'

const MENTION_REGEX = /@([a-zA-Z0-9._-]+)/g

function getUtf8ByteOffset(text: string, charIndex: number): number {
  return new TextEncoder().encode(text.slice(0, charIndex)).length
}

/** Add mention facets for verified custom handles (e.g. @News) that weren't resolved by detectFacets. */
export async function resolveVerifiedMentions(
  rt: RichText,
  fetchVerified: (handle: string) => Promise<VerifiedUser | null>,
): Promise<RichText> {
  const text = rt.text
  if (!text) return rt
  const existingMentionRanges = new Set<string>()
  if (rt.facets) {
    for (const facet of rt.facets) {
      const mention = facet.features.find(AppBskyRichtextFacet.isMention)
      if (mention?.did && facet.index) {
        existingMentionRanges.add(
          `${facet.index.byteStart}-${facet.index.byteEnd}`,
        )
      }
    }
  }
  const toAdd: {byteStart: number; byteEnd: number; did: string}[] = []
  for (const match of text.matchAll(MENTION_REGEX)) {
    const byteStart = getUtf8ByteOffset(text, match.index!)
    const byteEnd = getUtf8ByteOffset(text, match.index! + match[0].length)
    const key = `${byteStart}-${byteEnd}`
    if (existingMentionRanges.has(key)) continue
    const verified = await fetchVerified(match[1])
    if (verified?.did) {
      toAdd.push({byteStart, byteEnd, did: verified.did})
      existingMentionRanges.add(key)
    }
  }
  if (toAdd.length === 0) return rt
  rt = rt.clone()
  if (!rt.facets) rt.facets = []
  for (const {byteStart, byteEnd, did} of toAdd) {
    rt.facets!.push({
      index: {byteStart, byteEnd},
      features: [
        {$type: 'app.bsky.richtext.facet#mention', did} as const,
      ],
    })
  }
  return rt
}

export function shortenLinks(rt: RichText): RichText {
  if (!rt.facets?.length) {
    return rt
  }
  rt = rt.clone()
  // enumerate the link facets
  if (rt.facets) {
    for (const facet of rt.facets) {
      const isLink = !!facet.features.find(AppBskyRichtextFacet.isLink)
      if (!isLink) {
        continue
      }

      // extract and shorten the URL
      const {byteStart, byteEnd} = facet.index
      const url = rt.unicodeText.slice(byteStart, byteEnd)
      const shortened = new UnicodeString(toShortUrl(url))

      // insert the shorten URL
      rt.insert(byteStart, shortened.utf16)
      // update the facet to cover the new shortened URL
      facet.index.byteStart = byteStart
      facet.index.byteEnd = byteStart + shortened.length
      // remove the old URL
      rt.delete(byteStart + shortened.length, byteEnd + shortened.length)
    }
  }
  return rt
}

// filter out any mention facets that didn't map to a user
export function stripInvalidMentions(rt: RichText): RichText {
  if (!rt.facets?.length) {
    return rt
  }
  rt = rt.clone()
  if (rt.facets) {
    rt.facets = rt.facets?.filter(facet => {
      const mention = facet.features.find(AppBskyRichtextFacet.isMention)
      if (mention && !mention.did) {
        return false
      }
      return true
    })
  }
  return rt
}
